
/**
 * Calculates the sale amount based on cost and profit margin.
 *
 * @param {Object} params
 * @param {number|string} params.cost - The base cost of the item or service.
 * @param {number|string} params.profitMargin - The profit margin value (percentage or fixed amount).
 * @param {string} params.profitMarginType - Type of profit margin: 'PERCENTAGE' or 'FIXED'.
 * @returns {number} The calculated sale amount, rounded to two decimals.
 *
 * Use case: This function is used to determine the final sale price for a task, product, or service
 * in the system, ensuring correct calculation whether the margin is a percentage or a fixed value.
 */

import { toDecimal } from '../decimal-utils';

export const calculateSaleAmount = ({
  cost,
  profitMargin,
  profitMarginType,
}) => {
  const decimalCost = toDecimal(cost || 0);
  const decimalMargin = toDecimal(profitMargin || 0);

  if (decimalCost.isZero()) return 0;

  let saleAmount = decimalCost;

  if (profitMarginType === 'PERCENTAGE') {
    // Sale Amount = Cost * (1 + Margin/100)
    saleAmount = decimalCost.times(decimalMargin.dividedBy(100).plus(1));
  } else if (profitMarginType === 'FIXED') {
    // Sale Amount = Cost + Margin
    saleAmount = decimalCost.plus(decimalMargin);
  }

  // Ensure final value is rounded to two decimals and return as number
  return saleAmount.toDecimalPlaces(2).toNumber();
};
